import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeHasRoleSelector } from '../selector/authenticationSelector';
import Roles from './roles';
import { useAccessibleTenants } from './useAccessibleTenants';
import { useIsMaster } from './useIsMaster';

/**
 * Returns true when the authenticated user may access the all-tenant
 * (cross-tenant) operational view — the switcher entry, the `/master/*`
 * routes, and the cross-tenant tables all gate on this hook.
 *
 * Three roles qualify:
 *  - `master` always qualifies (internal staff with the full overview).
 *  - `receptionist` (our "service" role, e.g. call-center staff) and
 *    `manager` (e.g. second-level support) qualify only when they have
 *    more than one assigned tenant. Tenant-side staff (receptionists,
 *    caretakers) are scoped to a single tenant, so an all-tenant view
 *    would just repeat their one tenant — we hide it for them. Our
 *    internal support staff carry the same roles but are assigned several
 *    tenants, which is exactly the case the cross-tenant overview exists
 *    for.
 *
 * The cross-tenant pages fan out over `useAccessibleTenants()` (derived
 * from the JWT `tenant:<slug>` permissions), so no extra grant is needed
 * for these users — they aggregate exactly the tenants they're assigned.
 */
export const useCanViewAllTenants = (): boolean => {
    const isMaster = useIsMaster();
    const receptionistSelector = useMemo(
        () => makeHasRoleSelector(Roles.receptionist()),
        [],
    );
    const managerSelector = useMemo(
        () => makeHasRoleSelector(Roles.manager()),
        [],
    );
    const isReceptionist = useSelector(receptionistSelector);
    const isManager = useSelector(managerSelector);
    const accessibleTenants = useAccessibleTenants();

    return isMaster || ((isReceptionist || isManager) && accessibleTenants.length > 1);
};
